import { ArrowForward, Cancel } from '@mui/icons-material';
import { EdgeHandle } from '../common/EdgeHandle.tsx';
import { Node, Position, ReactFlowInstance, useEdges } from 'reactflow';
import { useStore } from '../../../../store/store.ts';
import { useCallback, useMemo } from 'react';
import { getNodeConfig } from '../../../../api/workflows-steps/formSchema.tsx';
import { EmbedWorkflowConfiguration, WorkflowNode } from '../../../../api/model.ts';
import { Typography } from '@mui/material';
import { NodeProps } from '../../../../types.ts';

const ProxyNode = ({ id }: NodeProps) => {
  const edges = useEdges();
  const mainReactFlowInstance: ReactFlowInstance | null = useStore().mainReactFlowInstance;
  const selectedEmbedWorkflows = useStore().selectedEmbedWorkflows;

  const getNodeById = useCallback(
    (nodeId: string) =>
      selectedEmbedWorkflows
        .map((w) => w.nodes)
        .flat()
        .find((n) => n.id === nodeId),
    [selectedEmbedWorkflows],
  );

  const handleRemoveConnection = useCallback(() => {
    mainReactFlowInstance?.setNodes((nodes) => nodes.filter((n) => n.id != id));
  }, [id, mainReactFlowInstance]);

  const { sourceNode, targetNode } = useMemo(() => {
    const sourceEdge = edges.find((e) => e.target === id);
    const targetEdge = edges.find((e) => e.source === id);
    if (!sourceEdge || !targetEdge) {
      return { sourceNode: null, targetNode: null };
    }

    const sourceNode = mainReactFlowInstance?.getNode(sourceEdge.source) as Node<WorkflowNode>;
    const sourceNodeConfig = sourceNode?.data.configuration as EmbedWorkflowConfiguration;

    const targetNode = mainReactFlowInstance?.getNode(targetEdge.target) as Node<WorkflowNode>;
    const targetNodeConfig = targetNode?.data.configuration as EmbedWorkflowConfiguration;

    if (!(sourceNodeConfig && targetNodeConfig)) {
      return { sourceNode: null, targetNode: null };
    }

    const sourceNodeId = sourceNodeConfig.output_node_mappings.find((m) => m.parent_node_id === id)?.child_node_id;
    const targetNodeId = targetNodeConfig.input_node_mappings.find((m) => m.parent_node_id === id)?.child_node_id;
    return {
      sourceNode: sourceNodeId ? getNodeById(sourceNodeId) : null,
      targetNode: targetNodeId ? getNodeById(targetNodeId) : null,
    };
  }, [mainReactFlowInstance, edges, getNodeById, id]);

  const { color: sourceColor, icon: sourceIcon } = getNodeConfig(sourceNode?.type ?? 'default') ?? {};
  const { color: targetColor, icon: targetIcon } = getNodeConfig(targetNode?.type ?? 'default') ?? {};

  return (
    <>
      <div className="flex justify-between items-center gap-0.5 hover:scale-125 transition-all hover:gap-1 group">
        <div
          className="relative flex items-center justify-center p-0.5 group-hover:p-2 rounded "
          style={{
            backgroundColor: sourceColor,
          }}
        >
          {sourceIcon}
          <Typography variant="caption" className="absolute -bottom-5 invisible group-hover:visible whitespace-nowrap">
            {sourceNode?.name}
          </Typography>
        </div>
        <div className="flex items-center">
          <ArrowForward className="opacity-50 group-hover:hidden" fontSize="small" />
          <Cancel
            color="error"
            className="opacity-50 hover:opacity-100 hidden group-hover:block hover:scale-110 cursor-pointer transition-all"
            fontSize="small"
            onClick={handleRemoveConnection}
          />
        </div>
        <div
          className="relative flex items-center justify-center p-0.5 group-hover:p-2 rounded "
          style={{
            backgroundColor: targetColor,
          }}
        >
          {targetIcon}
          <Typography variant="caption" className="absolute -top-5 invisible group-hover:visible whitespace-nowrap">
            {targetNode?.name}
          </Typography>
        </div>
      </div>
      <div className="invisible">
        <EdgeHandle type="source" position={Position.Right} isConnectable={true} />
        <EdgeHandle type="target" position={Position.Left} isConnectable={true} />
      </div>
    </>
  );
};

export default ProxyNode;
